import React, { useState, useMemo } from "react";
import { Avatar, Popover, Radio, Row, Skeleton, Col } from "antd";
import {
	Product,
	ProductPricingPlan,
	noOfDistributorsIntegrated,
	ProductSource,
	displayPricingName,
	Distributor
} from "@/Contracts/Products";

import { RadioChangeEvent } from "antd/lib/radio";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { getNarrowedCurrencySymbol } from "@/Components/Utils";
import { getProductImageUrl, shortenText } from "@/Utils";
import { currencyFormatter } from "@/Utils/Currency";
import {
	getInitialsForAvatar,
	getProductCatalogResultDisplayPrice
} from "@/Contracts/Utils";
import { ShowDistributorNotAvailableRadio } from "@/Components/DocumentsPage/Drawers/DocumentProductDrawers/DocumentProductDrawersComponents/ProductPricingOptions/ProductPricingOptions";

import "../../Drawers/ProductCatalogResultRow//ProductCatalogResultRow.less";

import ZTypography from "@atoms/ZTypography";
import ZTooltip from "@atoms/ZTooltip";
import PriceConversionTooltip from "@molecules/PriceConversionTooltip";
import ZButton from "@atoms/ZButton";
import { EFontWeights } from "@atoms/ZTypography/interface";
import he from "he";
import { ZIcon } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { VendorSubscriptionStatus } from "@/Contracts/Marketplace/Vendor";
import { vendorSubscriptionTag } from "@/V2Utils/Marketplace";
import { onJoinVendorClick } from "@/V2Utils/Marketplace";
import { ZMessage } from "@molecules";
import UsersActionCreators from "@/Actions/User";
import useDispatch from "@/Components/Utils/useDispatch";
import MarketplaceActionCreators from "@/Actions/Marketplace";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { getItemViewForProduct } from "@/Contracts/Products/UnifiedSearchUtils";
import ZImage from "@atoms/ZImage";
import amazonPrimeLogo from "config/assets/Logos/Prime.png";
import { ZomentumPSA } from "@/Contracts";

const { ZText } = ZTypography;

interface Props {
	readonly record: Product;
	readonly showPricing: boolean;
	readonly selectedPricingId?: string | null;
	readonly onPricingChange?: (
		productId: string,
		selectedPricingId?: string
	) => void;
	readonly isFromProductsPage?: boolean;
	readonly currentItemSource?: ProductSource;
	readonly pricingLoading?: boolean;
	readonly showUpdateMandatoryFieldsColumn?: boolean;
}

interface PricingSkeletonProps {
	readonly loading: boolean;
}

const PricingSkeleton: React.FC<PricingSkeletonProps> = props => {
	if (props.loading) {
		return (
			<div className="text-right">
				<Skeleton.Input active size="small" style={{ width: 150 }} />
			</div>
		);
	}
	return <>{props.children}</>;
};

const getPricingConversionInfo = (pricing: ProductPricingPlan) => {
	const currencyExchangeDetails = pricing.currency_exchange_details;
	const fromCurrency = `${
		currencyExchangeDetails?.from
	} ${getNarrowedCurrencySymbol(currencyExchangeDetails?.from ?? "")}`;
	const toCurrency = `${
		currencyExchangeDetails?.to
	} ${getNarrowedCurrencySymbol(currencyExchangeDetails?.to ?? "")}`;

	return (
		<div className="currency-exchange-details mx-0.5">
			<Row>
				{fromCurrency}
				{currencyExchangeDetails?.original.sell_price} = {toCurrency}
				{pricing.sell_price}
			</Row>
			<Row className="text-xs mt-0.5">
				<Col span={21}>
					{fromCurrency}1.00 = {toCurrency}
					{currencyExchangeDetails?.rate}
				</Col>
				<Col span={3}>
					<a href="#" className="text-right justify-end">
						<ZIcon
							name={EIcons.EXTERNALLINK}
							color={EColors.BLUE}
							colorVariant={1}
							size="sm"
						/>
					</a>
				</Col>
			</Row>
		</div>
	);
};

const ProductCatalogResultRow: React.FC<Props> = props => {
	const dispatch = useDispatch();
	const currentProduct = props.record;
	const { userCompany, vendorProgramStatus } = useSelector(
		(state: GlobalState) => {
			return {
				userCompany: state.user.userCompany,
				vendorProgramStatus: state.marketplace.vendorProgamDetails
			};
		}
	);
	const integrations = userCompany.integrations;
	const numberOfDistributorsIntegrated =
		noOfDistributorsIntegrated(integrations);
	const [isVendorRequestLoading, setIsVendorRequestLoading] =
		useState<boolean>(false);

	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const unifiedProductSearchFlag = useFeatureFlag("unified_product_search");

	const hasShowMoreButton = () => {
		return props.record?.pricing?.length > 1;
	};
	const showUpdateMandatoryFieldsColumn =
		props.showUpdateMandatoryFieldsColumn ?? false;

	const onPricingIdChange = (pricingId: string) => {
		props.onPricingChange &&
			props.onPricingChange(props.record.id, pricingId);
	};

	const selectedPricing = useMemo(() => {
		return props.record.pricing?.find(
			pricing => pricing.id === props.selectedPricingId
		);
	}, [props]);

	const resolvedItemView = useMemo(() => {
		if (!unifiedProductSearchFlag) return null;
		return getItemViewForProduct(props.record, props.selectedPricingId);
	}, [unifiedProductSearchFlag, props.record, props.selectedPricingId]);

	// For custom distributor products, use pricing data for display
	const isCustomDistributorProduct =
		props.currentItemSource === ProductSource.CustomDistributor;

	const displayName = useMemo(() => {
		if (unifiedProductSearchFlag) {
			return resolvedItemView?.name || props.record.name;
		}
		if (isCustomDistributorProduct && selectedPricing) {
			return selectedPricing.name || props.record.name;
		}
		return props.record.name;
	}, [
		unifiedProductSearchFlag,
		resolvedItemView,
		isCustomDistributorProduct,
		selectedPricing,
		props.record.name
	]);

	const displayDescription = useMemo(() => {
		if (unifiedProductSearchFlag) {
			return resolvedItemView?.description || props.record.description;
		}
		if (isCustomDistributorProduct && selectedPricing) {
			return selectedPricing.description || props.record.description;
		}
		return props.record.description;
	}, [
		unifiedProductSearchFlag,
		resolvedItemView,
		isCustomDistributorProduct,
		selectedPricing,
		props.record.description
	]);

	const displayImage = useMemo(() => {
		if (unifiedProductSearchFlag && resolvedItemView) {
			return (
				resolvedItemView.item_images?.[0] ||
				props.record?.logo ||
				props.record?.item_images?.[0] ||
				""
			);
		}
		if (
			selectedPricing?.distributor_pricing_details?.distributor ===
				Distributor.CustomDistributor &&
			selectedPricing?.images?.[0]
		) {
			return selectedPricing.images[0];
		}
		return props.record?.logo || props.record?.item_images?.[0] || "";
	}, [
		unifiedProductSearchFlag,
		resolvedItemView,
		selectedPricing,
		props.record?.logo,
		props.record?.item_images
	]);

	const displayManufacturerPartNo = useMemo(() => {
		if (unifiedProductSearchFlag && resolvedItemView) {
			return (
				resolvedItemView.manufacturer_part_no ??
				props.record.manufacturer_part_no
			);
		}
		return props.record.manufacturer_part_no;
	}, [
		unifiedProductSearchFlag,
		resolvedItemView,
		props.record.manufacturer_part_no
	]);

	const isMarketplaceSku =
		props.record.product_source === ProductSource.ZomentumMarketplace;
	const currentProductVendorStatus =
		vendorProgramStatus.find(v => {
			return v.id === currentProduct.vendor_company_id;
		})?.status ?? VendorSubscriptionStatus.NOTAPPLIED;

	const isSkuDisabled =
		isMarketplaceSku &&
		!props.showUpdateMandatoryFieldsColumn &&
		currentProductVendorStatus !== VendorSubscriptionStatus.ACCEPTED;

	const isAmazonDistributor =
		props.record.product_source === ProductSource.Amazon ||
		props.record?.sync_data?.[0]?.synced_from === ZomentumPSA.Amazon;

	const shouldShowPartNo = useMemo(() => {
		if (isMarketplaceSku || isAmazonDistributor) {
			return false;
		}
		return displayManufacturerPartNo != null;
	}, [isMarketplaceSku, isAmazonDistributor, displayManufacturerPartNo]);

	const onJoinVendor = async (
		vendorCompanyId?: string | null,
		vendorName?: string
	) => {
		setIsVendorRequestLoading(true);
		if (!vendorCompanyId) {
			ZMessage.error("Invalid Vendor ID for SKU");
			setIsVendorRequestLoading(false);
			return;
		}
		await onJoinVendorClick(vendorCompanyId, vendorName);
		await dispatch(UsersActionCreators.getUserCompany(false));
		if (showPartnerAlignFeaturesFlag) {
			dispatch(MarketplaceActionCreators.getVendorProgramStatusData());
		}
		setIsVendorRequestLoading(false);
	};

	const SkuVendorStatusOrJoinButton = () => {
		return (
			<Row justify="start" align="middle">
				{currentProductVendorStatus ===
				VendorSubscriptionStatus.NOTAPPLIED ? (
					<Row align="middle">
						<Col span={24} className="inline-block">
							<ZButton
								type="primary"
								ghost
								size="small"
								onClick={() =>
									onJoinVendor(
										currentProduct.vendor_company_id,
										currentProduct.pricing?.[0].vendor_name
									)
								}
								loading={isVendorRequestLoading}
								disabled={isVendorRequestLoading}
							>
								Join Vendor
							</ZButton>
							<ZTooltip
								title={`Join ${
									currentProduct.pricing?.[0]?.vendor_name ??
									`the vendor`
								} to add their SKU.`}
								placement="topLeft"
								getPopupContainer={trigger =>
									trigger.parentNode as HTMLElement
								}
							>
								<ZIcon
									name={EIcons.INFOCIRCLE}
									size="sm"
									className="ml-2 inline-block"
									colorVariant={7}
								/>
							</ZTooltip>
						</Col>
					</Row>
				) : (
					<>
						{vendorSubscriptionTag(currentProductVendorStatus)}
						{currentProductVendorStatus ===
							VendorSubscriptionStatus.APPLIED && (
							<ZTooltip
								title={`Waiting for ${
									currentProduct.pricing?.[0]?.vendor_name ??
									`the vendor`
								} to accept.`}
								placement="topLeft"
								getPopupContainer={trigger =>
									trigger.parentNode as HTMLElement
								}
							>
								<ZIcon
									name={EIcons.INFOCIRCLE}
									size="sm"
									className="ml-1 inline-block"
									colorVariant={7}
								/>
							</ZTooltip>
						)}
					</>
				)}
			</Row>
		);
	};

	const amazonProductPricingDetail =
		props.record.pricing[0]?.amazon_business_pricing_details;
	const amazonPricingVATlabel =
		selectedPricing?.amazon_business_pricing_details?.tax_exclusive_price
			.label;

	return (
		<Row
			className={`product-row-container ${
				isSkuDisabled ? "cursor-not-allowed" : ""
			}`}
		>
			<Col span={2}>
				<Avatar
					className={`product-name-icon ${
						isSkuDisabled ? "grey-image" : ""
					}`}
					alt="Product"
					src={getProductImageUrl(displayImage)}
					shape="square"
				>
					{getInitialsForAvatar(displayName)}
				</Avatar>
			</Col>
			<Col span={15} className="px-1">
				<Row>
					<Col span={24}>
						<Row align="middle" className="flex-1">
							<Col
								span={
									isMarketplaceSku &&
									currentProductVendorStatus !==
										VendorSubscriptionStatus.ACCEPTED
										? 16
										: 24
								}
							>
								<Row className="product-description-container mb-2">
									<Col span={24}>
										<ZTooltip title={displayName}>
											<ZText
												colorVariant={
													isSkuDisabled ? 7 : 10
												}
												weight={EFontWeights.MEDIUM}
												className="text-ellipsis"
											>
												{displayName
													? shortenText(
															displayName,
															50
													  )
													: "--"}
											</ZText>
										</ZTooltip>
									</Col>
									<Col span={24}>
										<ZText
											level={0}
											colorVariant={isSkuDisabled ? 7 : 8}
											className="product-description"
										>
											{displayDescription
												? he.decode(displayDescription)
												: "--"}
										</ZText>
									</Col>
								</Row>
							</Col>
							{isSkuDisabled && (
								<Col span={8}>
									<SkuVendorStatusOrJoinButton />
								</Col>
							)}
						</Row>
					</Col>

					<div className="flex flex-wrap w-full">
						{shouldShowPartNo && (
							<>
								<ZTooltip
									title={`Part no: ${
										displayManufacturerPartNo
											? displayManufacturerPartNo
											: "--"
									}`}
									placement="topLeft"
								>
									<ZText
										level={0}
										colorVariant={isSkuDisabled ? 7 : 8}
									>
										Part no :{" "}
										{displayManufacturerPartNo
											? displayManufacturerPartNo
											: "--"}
									</ZText>
								</ZTooltip>
								<ZText
									level={0}
									colorVariant={isSkuDisabled ? 7 : 8}
									className="mx-4"
								>
									|
								</ZText>
							</>
						)}
						<ZTooltip
							title={`${
								isAmazonDistributor ? "ASIN No" : "Supplier SKU"
							}: ${
								isAmazonDistributor
									? props.record.asin ?? "--"
									: selectedPricing?.vendor_sku_no ?? "--"
							}`}
							placement="topLeft"
						>
							<ZText
								level={0}
								colorVariant={isSkuDisabled ? 7 : 8}
							>
								{isAmazonDistributor
									? "ASIN No"
									: "Supplier SKU"}
								:{" "}
								{isAmazonDistributor
									? props.record.asin ?? "--"
									: selectedPricing?.vendor_sku_no ?? "--"}
							</ZText>
						</ZTooltip>
					</div>
				</Row>
			</Col>
			<Col span={7} className="pr-2">
				{props.showPricing && (
					<PricingSkeleton
						loading={
							!!props.pricingLoading &&
							props.record.pricing.length === 0
						}
					>
						{selectedPricing && (
							<div
								className={`product-pricing-container ${
									showUpdateMandatoryFieldsColumn
										? "mr-6"
										: ""
								}`}
							>
								<div className="text-right w-full">
									<ZTooltip
										title={displayPricingName(
											selectedPricing
										)}
									>
										<ZText className="product-pricing-name truncate inline-block">{`${displayPricingName(
											selectedPricing
										)}`}</ZText>
									</ZTooltip>
									<Row
										justify="end"
										align="middle"
										className="text-right"
									>
										<Col
											span={
												!!selectedPricing.currency_exchange_details
													? 20
													: 24
											}
											className="flex justify-end items-center"
										>
											{(() => {
												const formattedPrice = currencyFormatter(
													getProductCatalogResultDisplayPrice(
														selectedPricing,
														props.currentItemSource
													),
													"Not available"
												);
												const priceNode = (
													<ZText
														level={3}
														weight={EFontWeights.MEDIUM}
														colorVariant={
															isSkuDisabled ? 7 : 9
														}
														className="product-pricing"
													>{`${formattedPrice}`}</ZText>
												);
												return formattedPrice ===
													"Not available" ? (
													priceNode
												) : (
													<PriceConversionTooltip>
														{priceNode}
													</PriceConversionTooltip>
												);
											})()}
											{amazonPricingVATlabel && (
												<ZText
													level={2}
													weight={EFontWeights.MEDIUM}
													className="whitespace-nowrap"
												>
													&nbsp;
													{amazonPricingVATlabel}
												</ZText>
											)}
											{isAmazonDistributor &&
												amazonProductPricingDetail?.is_prime && (
													<ZImage
														width={57}
														height={16}
														preview={false}
														alt="Prime Tag"
														className={`mb-1 ml-2`}
														src={amazonPrimeLogo}
													/>
												)}
										</Col>
										{isAmazonDistributor && (
											<>
												<Col>
													<ZText
														level={0}
														weight={
															EFontWeights.MEDIUM
														}
													>
														{
															amazonProductPricingDetail?.delivery_information
														}
													</ZText>
												</Col>
												{amazonProductPricingDetail?.shipping_cost && (
													<Col>
														<ZText level={0}>
															{`Additional Shipping cost: ${currencyFormatter(
																amazonProductPricingDetail?.shipping_cost
															)}`}
														</ZText>
													</Col>
												)}
											</>
										)}
										{!!selectedPricing.currency_exchange_details && (
											<Col span={4}>
												<ZTooltip
													title={getPricingConversionInfo(
														selectedPricing
													)}
													getPopupContainer={trigger =>
														trigger.parentNode as HTMLElement
													}
													placement="top"
												>
													<ZIcon
														name={EIcons.INFOCIRCLE}
														size="sm"
														className="ml-1"
														colorVariant={7}
													/>
												</ZTooltip>
											</Col>
										)}
									</Row>
								</div>
								{hasShowMoreButton() && (
									<DistributorPricingPopover
										pricingDetails={props.record.pricing}
										selectedPricing={selectedPricing}
										noOfDistributorsIntegrated={
											numberOfDistributorsIntegrated
										}
										onChange={onPricingIdChange}
										isFromProductsPage={
											props.isFromProductsPage
										}
										currentItemSource={
											isAmazonDistributor
												? ProductSource.Amazon
												: props.currentItemSource
										}
										disabled={isSkuDisabled}
									/>
								)}
								{selectedPricing.distributor_pricing_details !=
									null && (
									<div className="product-pricing-stock-level text-right">{`Stock Level: ${selectedPricing.distributor_pricing_details.total_availability}/${selectedPricing.distributor_pricing_details.total_on_order}`}</div>
								)}
							</div>
						)}
						{!selectedPricing &&
							!props.pricingLoading &&
							props.record.pricing.length === 0 && (
								<div className="text-right">
									<ZText
										level={0}
										weight={EFontWeights.MEDIUM}
										color={EColors.GRAY}
									>
										No Pricing Available
									</ZText>
								</div>
							)}
					</PricingSkeleton>
				)}
				{props.children}
			</Col>
		</Row>
	);
};

interface DistributorPricingPopoverProps {
	readonly pricingDetails: ProductPricingPlan[];
	readonly selectedPricing: ProductPricingPlan;
	readonly noOfDistributorsIntegrated: number;
	readonly onChange: (pricingId: string) => void;
	readonly isFromProductsPage?: boolean;
	readonly currentItemSource?: ProductSource;
	readonly disabled: boolean;
}

const DistributorPricingPopover: React.FC<
	DistributorPricingPopoverProps
> = props => {
	const [visible, setVisible] = useState(false);

	const onChange = (e: RadioChangeEvent) => {
		setVisible(false);
		props.onChange(e.target.value);
	};

	const catalogPricing = props.pricingDetails.filter(
		pricing => !!!pricing.distributor_pricing_details
	);

	const distributorPricing = props.pricingDetails.filter(
		pricing => !!pricing.distributor_pricing_details
	);

	return (
		<div
			className="pricing-list-popover-menu flex"
			onClick={e => e.stopPropagation()}
		>
			<Popover
				trigger="click"
				placement="bottomRight"
				overlayClassName="pricing-list-popover"
				className="pricing-list-popover-label"
				onVisibleChange={setVisible}
				visible={visible}
				mouseLeaveDelay={0.4}
				content={
					<Radio.Group
						name="radiogroup"
						defaultValue={props.selectedPricing.id}
						onChange={onChange}
					>
						{catalogPricing.length > 0 && (
							<div className="pricing-sub-header">Catalog</div>
						)}
						{catalogPricing.map(pricing => {
							return (
								<Row key={pricing.id || ""}>
									<Radio
										value={pricing.id}
										className="pricing-list-radio"
									>
										<span className="distributor-pricing-area">
											<ZTypography.ZText className="distributor-name truncate max-w-xs">
												{pricing.name}
											</ZTypography.ZText>
											{(() => {
												const formattedPrice = currencyFormatter(
													getProductCatalogResultDisplayPrice(
														pricing,
														props.currentItemSource
													),
													"Not available"
												);
												const priceNode = (
													<span className="distributor-pricing">
														{formattedPrice}
													</span>
												);
												return formattedPrice ===
													"Not available" ? (
													priceNode
												) : (
													<PriceConversionTooltip>
														{priceNode}
													</PriceConversionTooltip>
												);
											})()}
										</span>
									</Radio>
								</Row>
							);
						})}
						<>
							{props.noOfDistributorsIntegrated > 0 && (
								<div className="pricing-sub-header">
									Distributor
								</div>
							)}
							{distributorPricing.map(pricing => {
								const pricingName = displayPricingName(pricing);
								const stockLevel =
									pricing.distributor_pricing_details
										? `${pricing.distributor_pricing_details.total_availability}/${pricing.distributor_pricing_details.total_on_order}`
										: "NA";
								return (
									<Row key={pricing.id || ""}>
										<Radio
											value={pricing.id}
											className="pricing-list-radio"
										>
											<span className="distributor-pricing-area">
												<span className="distributor-name">
													{pricingName}
												</span>
												{(() => {
													const formattedPrice = currencyFormatter(
														getProductCatalogResultDisplayPrice(
															pricing,
															props.currentItemSource
														),
														"Not available"
													);
													const priceNode = (
														<span className="distributor-pricing">
															{formattedPrice}
														</span>
													);
													return formattedPrice ===
														"Not available" ? (
														priceNode
													) : (
														<PriceConversionTooltip>
															{priceNode}
														</PriceConversionTooltip>
													);
												})()}
												<br />
												{pricing.distributor_pricing_details && (
													<span className="distributor-stock">{`Stock Level: ${stockLevel}`}</span>
												)}
											</span>
										</Radio>
									</Row>
								);
							})}
							<ShowDistributorNotAvailableRadio
								pricing={distributorPricing}
							/>
						</>
					</Radio.Group>
				}
			>
				<ZButton
					disabled={props.disabled}
					className="text-0 ml-auto"
					size="small"
				>
					Select more prices
				</ZButton>
			</Popover>
		</div>
	);
};

export default ProductCatalogResultRow;
